// STK1 (docs/build-briefs/STK1-86-setter.md; DEC-269). The pure half of the
// 86 list: which items are out, which can be marked out, the "Out since"
// wording, and the editor's wording. DOM-free for the reason sizeTokens.ts
// and inventoryListSort.ts give: main.ts and admin.ts run top-level
// mustFind calls at import, so a plain node:test file cannot import logic
// defined inline in either.

import { resolveActorDisplayName } from "./actorDisplay.ts";
import { floorQuantityState } from "./inventoryListSort.ts";
import { compareNames } from "./nameSort.ts";

/** The slice of a liquor product this module reads. */
export interface Stock86Product {
  id: string;
  name: string;
  category: string;
  flagged86: boolean;
  quantityState: "out_of_stock" | "low" | "ok" | null;
  /** NIGHTFIX1 (3): this location's par and the on-hand count in the same unit, where the list carries them. */
  par_level?: string | null;
  onHandInCountUnit?: string | null;
}

/** When and by whom an item was last marked out. Null name means the
 * setter's account no longer resolves ("Former employee"). */
export interface Stock86Since {
  since: Date;
  actorName: string | null;
}

/** "6:15 PM" in the device's own locale and zone. */
export function formatStock86Time(when: Date): string {
  return when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** The "Out right now" row's sub line, "Out since 6:15 PM, Priya N.".
 * `info` is undefined when nothing is known about the flag (the session
 * may not read the history, DEC-050), in which case the line is simply
 * absent rather than a guess. */
export function outSinceText(info: Stock86Since | undefined): string | undefined {
  if (info === undefined) {
    return undefined;
  }
  return `Out since ${formatStock86Time(info.since)}, ${resolveActorDisplayName(info.actorName)}`;
}

/** The item editor's line under the control, "86'd since 6:15 PM by Priya N.
 * Bartenders cannot pick it." Undefined when nothing is known. */
export function editorOutSinceText(info: Stock86Since | undefined): string | undefined {
  if (info === undefined) {
    return undefined;
  }
  // A display name such as "Priya N." already ends in a period; adding
  // another would read "Priya N.. Bartenders".
  const name = resolveActorDisplayName(info.actorName);
  const sentenceEnd = name.endsWith(".") ? "" : ".";
  return `86'd since ${formatStock86Time(info.since)} by ${name}${sentenceEnd} Bartenders cannot pick it.`;
}

/** Items out right now: freshest first (an item whose time is known sorts
 * ahead of one whose time is not, then by name), so a row just marked out
 * lands at the top. */
export function outRightNow<T extends Stock86Product>(
  products: readonly T[],
  sinceById: ReadonlyMap<string, Stock86Since>,
): T[] {
  return products
    .filter((product) => product.flagged86)
    .sort((a, b) => {
      const aSince = sinceById.get(a.id)?.since.getTime();
      const bSince = sinceById.get(b.id)?.since.getTime();
      if (aSince !== undefined && bSince !== undefined && aSince !== bSince) {
        return bSince - aSince;
      }
      if (aSince !== undefined && bSince === undefined) return -1;
      if (aSince === undefined && bSince !== undefined) return 1;
      return compareNames(a.name, b.name);
    });
}

/** Items that can be marked out: not already out, optionally narrowed by a
 * category chip (empty string is All) and a name search (case-insensitive
 * substring). Alphabetical. */
export function markOutCandidates<T extends Stock86Product>(
  products: readonly T[],
  filter: { category: string; query: string },
): T[] {
  const needle = filter.query.trim().toLowerCase();
  return products
    .filter((product) => !product.flagged86)
    .filter((product) => filter.category === "" || product.category === filter.category)
    .filter((product) => needle === "" || product.name.toLowerCase().includes(needle))
    .sort((a, b) => compareNames(a.name, b.name));
}

/** The category chips: only categories some active item actually has, so a
 * chip never leads to an empty list. Sorted by the label the caller shows. */
export function candidateCategories(
  products: readonly Stock86Product[],
  labelFor: (category: string) => string,
): string[] {
  const seen = new Set<string>();
  for (const product of products) {
    seen.add(product.category);
  }
  return [...seen].sort((a, b) => compareNames(labelFor(a), labelFor(b)));
}

/** The panel's "Mark something out" sub line: the category, plus ", Low"
 * for a low item. */
export function candidateSubText(product: Stock86Product, categoryLabel: string): string {
  return floorQuantityState(product.quantityState, product.onHandInCountUnit, product.par_level) === "low"
    ? `${categoryLabel}, Low`
    : categoryLabel;
}

/** Folds the history rows into one entry per currently-out product. Rows are
 * newest first; the first row seen per product is its latest set or clear,
 * and only a SET (flagged true) yields an entry. */
export function sinceByProduct(
  rows: readonly {
    liquorProductId: string;
    flagged: boolean;
    actorName: string | null;
    createdAt: string;
  }[],
): Map<string, Stock86Since> {
  const seen = new Set<string>();
  const result = new Map<string, Stock86Since>();
  for (const row of rows) {
    if (seen.has(row.liquorProductId)) {
      continue;
    }
    seen.add(row.liquorProductId);
    if (row.flagged) {
      result.set(row.liquorProductId, {
        since: new Date(row.createdAt),
        actorName: row.actorName,
      });
    }
  }
  return result;
}

/** The toast wording, one place so the e2e and the code cannot drift. */
export function markedOutToast(name: string): string {
  return `${name} is 86'd.`;
}
export function backInStockToast(name: string): string {
  return `86 cleared for ${name}.`;
}

// ---------------------------------------------------------------------
// CORE7 (docs/build-briefs/CORE7-86-any-item.md). The 86 list names three
// kinds of thing: an inventory item (a product), a menu drink, an
// ingredient. Every function above reads `id`, `name`, `category`,
// `flagged86` and `quantityState`, so an entry for a drink or an
// ingredient carries a KEY in `id` that cannot collide with a product's
// uuid, and the same helpers (out list order, candidates, chips, since
// folding) work on all three unchanged.
// ---------------------------------------------------------------------

export type Stock86Kind = "product" | "drink" | "ingredient";

/** Chip values for the two non-product kinds. Underscored so they can never equal a real item category. */
export const STOCK86_DRINKS_CATEGORY = "__drinks";
export const STOCK86_INGREDIENTS_CATEGORY = "__ingredients";

export interface Stock86Entry extends Stock86Product {
  kind: Stock86Kind;
  /** The product id, the menu drink id, or the ingredient key: what the server is told. */
  subjectId: string;
  /** This entry's OWN flag. `flagged86` is the effective one: a drink is also 86'd through an ingredient. */
  own: boolean;
  /** For a drink 86'd through an ingredient or mapped item, their names. */
  via: string[];
  /** For an ingredient, how many active drinks use it. */
  usedByCount: number;
}

export function stock86DrinkKey(menuDrinkId: string): string {
  return `drink:${menuDrinkId}`;
}
export function stock86IngredientKey(ingredientKey: string): string {
  return `ingredient:${ingredientKey}`;
}

/** The chip and row wording for a category value (the caller supplies the product category labeller). */
export function stock86CategoryLabel(
  category: string,
  productLabel: (value: string) => string,
): string {
  if (category === STOCK86_DRINKS_CATEGORY) return "Drinks";
  if (category === STOCK86_INGREDIENTS_CATEGORY) return "Ingredients";
  return productLabel(category);
}

export type Stock86ProductInput = Stock86Product;
export interface Stock86DrinkInput {
  menuDrinkId: string;
  name: string;
  own: boolean;
  via: string[];
  flagged86: boolean;
}
export interface Stock86IngredientInput {
  key: string;
  label: string;
  flagged86: boolean;
  usedByCount: number;
}

function slugOf(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** LOUNGERMAJ1 (lounger-15): an ingredient's label is the reference a recipe stores ("beer-copper-kettle-ipa",
 * "coffee-liqueur"), which is a slug, not a name. A reference that names an inventory item (its name, or its category and
 * name) reads as that item's own name; any other slug reads as plain words. A label that already has a space or a capital
 * is a name and is left as it is. */
export function ingredientDisplayName(
  label: string,
  products: readonly { name: string; category: string }[],
): string {
  if (/[\sA-Z]/.test(label) || !/[-_]/.test(label)) {
    return label;
  }
  const slug = slugOf(label);
  for (const product of products) {
    const nameSlug = slugOf(product.name);
    if (slug === nameSlug || slug === `${slugOf(product.category)}-${nameSlug}`) {
      return product.name;
    }
  }
  const words = label.replace(/[-_]+/g, " ").trim();
  return words === "" ? label : words.charAt(0).toUpperCase() + words.slice(1);
}

/** One list of entries from the three sources. */
export function stock86Entries(
  products: readonly Stock86ProductInput[],
  drinks: readonly Stock86DrinkInput[],
  ingredients: readonly Stock86IngredientInput[],
): Stock86Entry[] {
  return [
    ...products.map((product): Stock86Entry => ({
      ...product,
      kind: "product",
      subjectId: product.id,
      own: product.flagged86,
      via: [],
      usedByCount: 0,
    })),
    ...drinks.map((drink): Stock86Entry => ({
      id: stock86DrinkKey(drink.menuDrinkId),
      name: drink.name,
      category: STOCK86_DRINKS_CATEGORY,
      flagged86: drink.flagged86,
      quantityState: null,
      kind: "drink",
      subjectId: drink.menuDrinkId,
      own: drink.own,
      via: drink.via,
      usedByCount: 0,
    })),
    ...ingredients.map((ingredient): Stock86Entry => ({
      id: stock86IngredientKey(ingredient.key),
      name: ingredientDisplayName(ingredient.label, products),
      category: STOCK86_INGREDIENTS_CATEGORY,
      flagged86: ingredient.flagged86,
      quantityState: null,
      kind: "ingredient",
      subjectId: ingredient.key,
      own: ingredient.flagged86,
      via: [],
      usedByCount: ingredient.usedByCount,
    })),
  ];
}

/** "Out of stock": a product with nothing on hand that nobody 86'd. This is the tile's OUT badge, and the list
 * section of that name, so the two always agree. An 86'd product is in "Out right now" instead, whatever its stock. */
export function outOfStockEntries<T extends Stock86Entry>(entries: readonly T[]): T[] {
  return entries
    .filter(
      (entry) =>
        entry.kind === "product" && !entry.flagged86 && entry.quantityState === "out_of_stock",
    )
    .sort((a, b) => compareNames(a.name, b.name));
}

/** The sub line of a row in "Out right now": who and when where known, else why (a drink 86'd through an
 * ingredient), else nothing. */
export function outRowSubText(
  entry: Stock86Entry,
  info: Stock86Since | undefined,
): string | undefined {
  if (entry.own) {
    return outSinceText(info);
  }
  return entry.via.length === 0
    ? undefined
    : `86'd because ${joinNames(entry.via)} ${entry.via.length === 1 ? "is" : "are"} 86'd`;
}

/** The sub line of a row in "Mark something out". */
export function candidateEntrySubText(entry: Stock86Entry, productCategoryLabel: string): string {
  if (entry.kind === "drink") return "Drink";
  if (entry.kind === "ingredient") {
    return entry.usedByCount === 1
      ? "Ingredient, used in 1 drink"
      : `Ingredient, used in ${entry.usedByCount} drinks`;
  }
  return candidateSubText(entry, productCategoryLabel);
}

export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** since/who for drinks and ingredients, keyed like the entries. */
export function sinceFromMenuState(
  drinks: readonly {
    menuDrinkId: string;
    since: string | null;
    actorName: string | null;
    own: boolean;
  }[],
  ingredients: readonly {
    key: string;
    since: string | null;
    actorName: string | null;
    flagged86: boolean;
  }[],
): Map<string, Stock86Since> {
  const result = new Map<string, Stock86Since>();
  for (const drink of drinks) {
    if (drink.own && drink.since !== null) {
      result.set(stock86DrinkKey(drink.menuDrinkId), {
        since: new Date(drink.since),
        actorName: drink.actorName,
      });
    }
  }
  for (const ingredient of ingredients) {
    if (ingredient.flagged86 && ingredient.since !== null) {
      result.set(stock86IngredientKey(ingredient.key), {
        since: new Date(ingredient.since),
        actorName: ingredient.actorName,
      });
    }
  }
  return result;
}

/** Recomputes a drink's effective 86 after the client flips one flag locally: its own flag, the ingredient labels
 * that are 86'd among its recipe's keys, and the mapped item names the server reported. */
export function recomputeDrink86(params: {
  own: boolean;
  ingredientKeys: readonly string[];
  viaItems: readonly string[];
  flaggedIngredientLabels: ReadonlyMap<string, string>;
}): { flagged86: boolean; via: string[] } {
  const via = [
    ...params.viaItems,
    ...params.ingredientKeys
      .map((key) => params.flaggedIngredientLabels.get(key))
      .filter((label): label is string => label !== undefined),
  ];
  return { flagged86: params.own || via.length > 0, via };
}

// ---------------------------------------------------------------------
// CORE7-F1 (docs/build-briefs/CORE7-F1-86-visibility-in-settings.md). The pure half of: the 86 history under a list
// row, the Settings Stock levels rows for drinks and ingredients, and the offer to clear an 86 on stock-in.
// ---------------------------------------------------------------------

/** One stored set or clear of an 86. */
export interface Stock86HistoryEvent {
  flagged: boolean;
  actorName: string | null;
  at: Date;
}

/** Folds the three history reads into one map keyed like the entries (a product id, `drink:<id>`,
 * `ingredient:<key>`). Every list is newest first and stays so. */
export function historyByEntryKey(
  productRows: readonly {
    liquorProductId: string;
    flagged: boolean;
    actorName: string | null;
    createdAt: string;
  }[],
  menuRows: readonly {
    subjectKind: "menu_drink" | "ingredient";
    subjectId: string;
    flagged: boolean;
    actorName: string | null;
    createdAt: string;
  }[],
): Map<string, Stock86HistoryEvent[]> {
  const result = new Map<string, Stock86HistoryEvent[]>();
  const push = (key: string, event: Stock86HistoryEvent): void => {
    const list = result.get(key);
    if (list === undefined) result.set(key, [event]);
    else list.push(event);
  };
  for (const row of productRows) {
    push(row.liquorProductId, {
      flagged: row.flagged,
      actorName: row.actorName,
      at: new Date(row.createdAt),
    });
  }
  for (const row of menuRows) {
    push(
      row.subjectKind === "menu_drink"
        ? stock86DrinkKey(row.subjectId)
        : stock86IngredientKey(row.subjectId),
      {
        flagged: row.flagged,
        actorName: row.actorName,
        at: new Date(row.createdAt),
      },
    );
  }
  return result;
}

/** "86'd 6:15 PM, Priya N." or "86 cleared 7:02 PM, Priya N.". The day is added when it is not today. */
export function historyEventText(event: Stock86HistoryEvent, now: Date = new Date()): string {
  const sameDay = event.at.toDateString() === now.toDateString();
  const day = sameDay
    ? ""
    : `${event.at.toLocaleDateString([], { month: "short", day: "numeric" })} `;
  return `${event.flagged ? "86'd" : "86 cleared"} ${day}${formatStock86Time(event.at)}, ${resolveActorDisplayName(event.actorName)}`;
}

/** The most events one row shows. */
export const STOCK86_HISTORY_SHOWN = 5;

/** The lines under a row: newest first, capped, or a single note when nothing is stored. */
export function historyLines(
  events: readonly Stock86HistoryEvent[] | undefined,
  now: Date = new Date(),
): string[] {
  if (events === undefined || events.length === 0) return ["No 86 history yet."];
  return events.slice(0, STOCK86_HISTORY_SHOWN).map((event) => historyEventText(event, now));
}

/** A drink or ingredient row of Settings > Stock levels. `own` is the flag the control sets; `flagged86` the effective one. */
export interface SettingsMenuRow {
  kind: "drink" | "ingredient";
  subjectId: string;
  key: string;
  name: string;
  own: boolean;
  flagged86: boolean;
  via: string[];
}

/** The rows Stock levels shows after the items, for the search box and the chip. Only the "All" and "86'd only"
 * chips include them: Low, out and No par describe on-hand and par, which a drink or ingredient has none of. */
export function settingsMenuRows(
  entries: readonly Stock86Entry[],
  filter: { chip: "" | "low" | "86" | "noPar"; query: string },
): { drinks: SettingsMenuRow[]; ingredients: SettingsMenuRow[] } {
  const needle = filter.query.trim().toLowerCase();
  const rows = (kind: "drink" | "ingredient"): SettingsMenuRow[] =>
    filter.chip === "low" || filter.chip === "noPar"
      ? []
      : entries
          .filter((entry) => entry.kind === kind)
          .filter((entry) => filter.chip !== "86" || entry.flagged86)
          .filter((entry) => needle === "" || entry.name.toLowerCase().includes(needle))
          .sort((a, b) => compareNames(a.name, b.name))
          .map((entry) => ({
            kind,
            subjectId: entry.subjectId,
            key: entry.id,
            name: entry.name,
            own: entry.own,
            flagged86: entry.flagged86,
            via: entry.via,
          }));
  return { drinks: rows("drink"), ingredients: rows("ingredient") };
}

/** The Status cell of a drink or ingredient row: 86'd, or Not 86'd. */
export function settingsMenuRowStatus(row: SettingsMenuRow): "86'd" | "Not 86'd" {
  return row.flagged86 ? "86'd" : "Not 86'd";
}

/** The Stock cell button of a row: only a row whose own flag is the cause can be cleared there. A drink 86'd only
 * through an ingredient is cleared on the ingredient, so its cell says which. */
export function settingsMenuRowAction(
  row: SettingsMenuRow,
): { label: "Mark 86" | "Clear 86"; flagged: boolean } | { label: null; via: string } {
  if (row.own) return { label: "Clear 86", flagged: false };
  if (row.flagged86) return { label: null, via: joinNames(row.via) };
  return { label: "Mark 86", flagged: true };
}

/** Which of the received or counted products are 86'd right now, in the order given and without repeats. These
 * are the items a receipt or count offers to clear. */
export function clearOfferProducts<T extends { id: string; name: string; flagged86: boolean }>(
  touchedIds: readonly string[],
  products: readonly T[],
): T[] {
  const byId = new Map(products.map((product) => [product.id, product] as const));
  const seen = new Set<string>();
  const offers: T[] = [];
  for (const id of touchedIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const product = byId.get(id);
    if (product?.flagged86 === true) offers.push(product);
  }
  return offers;
}

/** The offer's words: the toast text for one item, or a count when a count touched several. */
export function clearOfferText(offers: readonly { name: string }[]): string {
  if (offers.length === 1) return `${offers[0]?.name} is 86'd.`;
  return `${offers.length} items are 86'd.`;
}

/** The drink and ingredient panels' line under the control: "86'd since 6:15 PM by Priya N." Undefined when
 * nothing is known. No second sentence (the item editor's wording carries one; these do not). */
export function menuEditorOutSinceText(info: Stock86Since | undefined): string | undefined {
  if (info === undefined) {
    return undefined;
  }
  const name = resolveActorDisplayName(info.actorName);
  return `86'd since ${formatStock86Time(info.since)} by ${name}${name.endsWith(".") ? "" : "."}`;
}
